import React from 'react';
import {
  Phone, Mail, MapPin, Calendar, BellRing, MessageSquare, FileText,
  ChevronDown, ChevronUp, Edit, XCircle, Building2, Send, Check,
  ShoppingCart, Users, Plus, Download, Trophy, ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const fmtDateTime = (ts) =>
  new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const fmtDate = (ts) =>
  new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

function ConvertedListRow({
  lead, getServiceBadge, fmtCurrency, navigate, openRevertModal,
}) {
  const svc = getServiceBadge(lead.service_type || 'probound');
  return (
    <div className="transition-colors p-4 md:p-6 bg-emerald-50/50 border-l-4 border-emerald-400" data-testid={`converted-list-${lead.id}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="font-bold text-emerald-900 truncate flex items-center gap-1.5">
              <Trophy className="w-4 h-4 text-emerald-600" />
              {lead.customer_name}
            </h3>
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: svc.color }}
            >
              {svc.label}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-600 text-white">
              <Trophy className="w-3 h-3" /> Converted to Project
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            {lead.customer_phone && (<span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" />{lead.customer_phone}</span>)}
            {lead.customer_email && (<span className="flex items-center gap-1 truncate"><Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /><span className="truncate">{lead.customer_email}</span></span>)}
            {lead.customer_address && (<span className="flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /><span className="truncate">{lead.customer_address}</span></span>)}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-emerald-900 mt-1.5">
            {lead.project_reference && (<span><strong>Project:</strong> {lead.project_reference}</span>)}
            {fmtCurrency(lead.project_value) && (<span><strong>Value:</strong> {fmtCurrency(lead.project_value)}</span>)}
            {lead.project_install_date && (<span><strong>Install:</strong> {lead.project_install_date}</span>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid={`go-to-project-list-${lead.id}`}
            onClick={() => lead.project_id && navigate(`/projects/${lead.project_id}`)}
            disabled={!lead.project_id}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
          >
            <Building2 className="w-4 h-4 mr-1.5" /> Go to project
          </Button>
          <Button
            data-testid={`revert-to-lead-list-${lead.id}`}
            onClick={() => openRevertModal(lead)}
            size="sm"
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-semibold"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Convert back to lead
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Expanded list view row for a lead. Click on the header collapses/expands the
 * lead. The expanded body shows lead details, call logs, quotes, and a stack
 * of action buttons.
 *
 * All handlers and badge helpers come from the parent (ManageLeads) so the
 * row stays purely presentational.
 */
export default function ListViewRow({
  lead,
  // state
  expandedLead, setExpandedLead,
  loadingQuotes, leadQuotes,
  isStaff,
  crmStatuses,
  // badge helpers
  getServiceBadge, getStatusBadge, getStatusIcon, getStatusLabel,
  fmtCurrency,
  // handlers
  navigate,
  openDetailModal, openCallLogModal, openEditModal, openRevertModal,
  handleStatusChange, handleConvertToProject, handleArchiveLead,
  handleDownloadQuotePDF, handleSendReminderEmail, handleClearReminder,
  setShowDetailModal,
}) {
  if (lead.status === 'won') {
    return (
      <ConvertedListRow
        lead={lead}
        getServiceBadge={getServiceBadge}
        fmtCurrency={fmtCurrency}
        navigate={navigate}
        openRevertModal={openRevertModal}
      />
    );
  }

  const svc = getServiceBadge(lead.service_type || 'probound');
  const expanded = expandedLead === lead.id;

  return (
    <div className="transition-colors" data-testid={`lead-${lead.id}`}>
      <div
        className="p-4 md:p-6 hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpandedLead(expanded ? null : lead.id)}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3
                onClick={(e) => { e.stopPropagation(); openDetailModal(lead); }}
                className="font-bold text-[#27292E] truncate cursor-pointer hover:text-[#006400] hover:underline"
              >
                {lead.customer_name}
              </h3>
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: svc.color }}
              >
                {svc.label}
              </span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(lead.status)}`}>
                {getStatusIcon(lead.status)}
                {getStatusLabel(lead.status)}
              </span>
              {lead.reminder_date && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  <BellRing className="w-3 h-3" />
                  {lead.reminder_date}
                </span>
              )}
              {lead.added_by && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                  Added by {lead.added_by_name || 'Ox Resin'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
              {lead.customer_phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 text-gray-400" />
                  {lead.customer_phone}
                </span>
              )}
              {lead.customer_address && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{lead.customer_address}</span>
                </span>
              )}
              {lead.project_size && (
                <span className="text-gray-500">Size: {lead.project_size}</span>
              )}
              {lead.call_logs && lead.call_logs.length > 0 && (
                <span className="flex items-center gap-1 text-gray-400">
                  <MessageSquare className="w-3.5 h-3.5" />
                  {lead.call_logs.length} note{lead.call_logs.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 md:px-6 pb-6 bg-gray-50 border-t border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-4">
            {/* Left - Lead Details */}
            <div className="space-y-4">
              <h4 className="font-semibold text-[#27292E]">Lead Details</h4>
              {lead.customer_email && (
                <div className="flex items-start gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400 mt-0.5" /><span>{lead.customer_email}</span>
                </div>
              )}
              {lead.project_timeline && (
                <div className="flex items-start gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5" /><span><strong>Timeline:</strong> {lead.project_timeline}</span>
                </div>
              )}
              {lead.project_description && (
                <div className="text-sm"><strong>Description:</strong>
                  <p className="text-gray-600 mt-1">{lead.project_description}</p>
                </div>
              )}
              {lead.appointment_date && (
                <div className="p-3 bg-[#2F5E96]/10 rounded-lg text-sm">
                  <strong className="text-[#2F5E96]">Appointment:</strong>
                  <p className="text-[#2F5E96]">{lead.appointment_date} {lead.appointment_time && `at ${lead.appointment_time}`}</p>
                </div>
              )}
              {lead.notes && (
                <div className="text-sm"><strong>Notes:</strong>
                  <p className="text-gray-600 mt-1 whitespace-pre-wrap">{lead.notes}</p>
                </div>
              )}
              <div className="pt-2">
                <Label className="text-sm font-medium mb-2 block">Change Status</Label>
                <select
                  value={lead.status}
                  onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                  className="w-full border-2 rounded-lg p-2.5 text-sm"
                >
                  {Object.entries(crmStatuses).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Middle - Call Logs */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-[#27292E]">Call Logs</h4>
                <Button
                  onClick={(e) => { e.stopPropagation(); openCallLogModal(lead); }}
                  size="sm"
                  className="bg-[#006400] hover:bg-[#004d00] text-white"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Note
                </Button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {lead.call_logs && lead.call_logs.length > 0 ? (
                  lead.call_logs.slice().reverse().map((log, idx) => (
                    <div key={log.id || idx} className="bg-white p-3 rounded-lg border border-gray-200 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{log.user_name}</span>
                        <span className="text-xs text-gray-400">{fmtDateTime(log.timestamp)}</span>
                      </div>
                      <p className="text-gray-600">{log.note}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400 text-sm bg-white rounded-lg border border-dashed border-gray-200">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" /> No call logs yet
                  </div>
                )}
              </div>
            </div>

            {/* Right - Quotes & Actions */}
            <div className="space-y-3">
              <h4 className="font-semibold text-[#27292E]">Quotes</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {loadingQuotes ? (
                  <div className="text-center py-4 text-gray-400 text-sm">Loading quotes...</div>
                ) : leadQuotes.length > 0 ? (
                  leadQuotes.map((quote) => (
                    <div key={quote.id} className="bg-white p-3 rounded-lg border border-gray-200 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-[#2F5E96]">{quote.reference}</span>
                        <span className="text-xs font-bold text-gray-700">
                          £{(quote.grand_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">{fmtDate(quote.created_at)}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadQuotePDF(quote); }}
                          className="text-xs text-[#2F5E96] hover:text-[#1e3a5f] font-medium flex items-center gap-1"
                          data-testid={`download-quote-${quote.id}`}
                        >
                          <Download className="w-3 h-3" /> PDF
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-400 text-sm bg-white rounded-lg border border-dashed border-gray-200">
                    <FileText className="w-6 h-6 mx-auto mb-1 opacity-50" /> No quotes yet
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-3 mt-3"></div>
              <h4 className="font-semibold text-[#27292E]">Actions</h4>

              {(lead.status === 'won' || lead.status === 'quoted') && (
                <Button
                  onClick={(e) => { e.stopPropagation(); handleConvertToProject(lead); }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white justify-start"
                  data-testid={`convert-project-${lead.id}`}
                >
                  <Building2 className="w-4 h-4 mr-2" /> Convert to Project
                </Button>
              )}

              <Button
                onClick={(e) => { e.stopPropagation(); navigate(`/quote-project?lead=${lead.id}`); }}
                className="w-full bg-[#006400] hover:bg-[#004d00] text-white justify-start"
              >
                <FileText className="w-4 h-4 mr-2" /> Send Quote
              </Button>

              <Button
                onClick={(e) => { e.stopPropagation(); navigate(`/order?lead=${lead.id}`); }}
                variant="outline"
                className="w-full border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5 justify-start"
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> Order Builder
              </Button>

              {isStaff && (
                <Button
                  onClick={(e) => { e.stopPropagation(); navigate(`/find-installer?lead_id=${lead.id}`); }}
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50 justify-start"
                  data-testid={`find-installer-${lead.id}`}
                >
                  <Users className="w-4 h-4 mr-2" /> Find Approved Installer
                </Button>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={(e) => { e.stopPropagation(); setShowDetailModal(false); openEditModal(lead); }}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  onClick={(e) => { e.stopPropagation(); handleArchiveLead(lead.id); setShowDetailModal(false); }}
                  variant="outline"
                  size="sm"
                  className="flex-1 text-gray-600 border-gray-200 hover:bg-gray-50"
                >
                  <XCircle className="w-4 h-4 mr-1" /> Archive
                </Button>
              </div>

              {lead.reminder_date && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <BellRing className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-amber-800">Reminder Set</span>
                  </div>
                  <p className="text-sm text-amber-700 mb-2"><strong>Date:</strong> {lead.reminder_date}</p>
                  {lead.reminder_note && (
                    <p className="text-sm text-amber-700 mb-3">{lead.reminder_note}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleSendReminderEmail(lead.id); }}
                      size="sm"
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Send className="w-3 h-3 mr-1" /> Email
                    </Button>
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleClearReminder(lead.id); }}
                      size="sm"
                      variant="outline"
                      className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      <Check className="w-3 h-3 mr-1" /> Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
